/**
 * colorPick — hex↔rgb() conversion + auto-contrast for the swatch palette
 * (ColorsEditor). The stored PassDesign colour model stays `rgb(r, g, b)`
 * strings (validatePassDesign contract untouched); this lib is the ONLY place
 * that talks hex, for the swatch grid + the native `<input type="color">`.
 *
 * The luminance math is ported from
 * `packages/wallet-apple/src/applePassViewModelAdapter.ts`'s `relativeLuminance`
 * (same ITU-R BT.601-style perceptual weighting: 0.299R + 0.587G + 0.114B) —
 * copied rather than imported so this app never crosses the package boundary
 * into wallet-apple. Normalized to a 0..1 scale here (that adapter works on
 * 0..255 with a threshold of 140) so the threshold reads as the WCAG-style 0.5
 * midpoint.
 */

/** Insurwiz navy — the fallback for any unparseable input. */
const FALLBACK_HEX = '#003366';

const WHITE_RGB = 'rgb(255, 255, 255)';
/** Tailwind slate-800 — value text on a light background. */
const DARK_FOREGROUND_RGB = 'rgb(30, 41, 59)';
/**
 * Tailwind slate-700 — label text on a light background (slightly muted vs.
 * value text). Deliberately NOT slate-600: against the gold swatch #f59e0b
 * slate-600 is only 3.53:1 (WCAG AA fail); slate-700 is 4.82:1 (≥ 4.5) and
 * still 10.35:1 on white.
 */
const DARK_LABEL_RGB = 'rgb(51, 65, 85)';

const LUMINANCE_THRESHOLD = 0.5;

export interface ContrastPair {
  foreground: string;
  label: string;
}

function clampByte(n: number): number {
  return Math.min(255, Math.max(0, Math.round(n)));
}

/** `#rrggbb` (case-insensitive, optional leading `#`) → `rgb(r, g, b)`. Falls back to Insurwiz navy. */
export function hexToRgbString(hex: string): string {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (m === null) return hexToRgbString(FALLBACK_HEX);
  const int = Number.parseInt(m[1] as string, 16);
  const r = clampByte((int >> 16) & 0xff);
  const g = clampByte((int >> 8) & 0xff);
  const b = clampByte(int & 0xff);
  return `rgb(${String(r)}, ${String(g)}, ${String(b)})`;
}

/** `rgb(r, g, b)` → `#rrggbb` (lowercase), or null when the input is not an rgb() string. */
function parseRgbToHex(rgb: string): string | null {
  const m = /^rgb\(\s*(-?\d+)\s*,\s*(-?\d+)\s*,\s*(-?\d+)\s*\)$/.exec(rgb.trim());
  if (m === null) return null;
  const r = clampByte(Number(m[1]));
  const g = clampByte(Number(m[2]));
  const b = clampByte(Number(m[3]));
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/** `rgb(r, g, b)` → `#rrggbb` (lowercase). Falls back to Insurwiz navy on any unparseable input. */
export function rgbStringToHex(rgb: string): string {
  return parseRgbToHex(rgb) ?? FALLBACK_HEX;
}

/**
 * Parses EITHER stored colour shape (`#rrggbb` or `rgb(r, g, b)` — the
 * validatePassDesign contract accepts both, so legacy designs may hold hex) to
 * a lowercase `#rrggbb`, or null when it is neither. Deliberately NO fallback:
 * the swatch grid must show a selection only on a GENUINE match — a fallback
 * hex would falsely ring the navy swatch for any unrecognized stored colour.
 */
export function normalizeToHex(color: string): string | null {
  const hex = /^#?([0-9a-fA-F]{6})$/.exec(color.trim());
  if (hex !== null) return `#${(hex[1] as string).toLowerCase()}`;
  return parseRgbToHex(color);
}

/**
 * Perceptual luminance of an `rgb(r, g, b)` string, normalized to 0..1.
 * Ported from applePassViewModelAdapter.ts's `relativeLuminance` (see file
 * header) — same coefficients, rescaled from the 0..255 domain. Unparseable
 * input reads as 0 (→ treated as a dark background, so autoContrastFor still
 * returns a safe white-on-dark pair rather than throwing).
 */
function relativeLuminance01(rgb: string): number {
  const m = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/.exec(rgb.trim());
  if (m === null) return 0;
  const r = Number(m[1]);
  const g = Number(m[2]);
  const b = Number(m[3]);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

/**
 * Derives a readable foreground/label pair for a `colors.background` value:
 * white text on a dark background, slate-800/slate-700 text on a light one.
 * WCAG-style relative luminance, threshold 0.5.
 */
export function autoContrastFor(backgroundRgb: string): ContrastPair {
  const isLight = relativeLuminance01(backgroundRgb) > LUMINANCE_THRESHOLD;
  return isLight
    ? { foreground: DARK_FOREGROUND_RGB, label: DARK_LABEL_RGB }
    : { foreground: WHITE_RGB, label: WHITE_RGB };
}
